import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr, uploadTenderDocument } from "@/lib/api";
import { TENDERAI } from "@/constants/testIds";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  FileText, Trash2, Sparkles, Loader2, UploadCloud, AlertTriangle, CheckCircle2,
  Save, ArrowUpRight, ShieldCheck, FileWarning, ListChecks, Hammer,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CHIP = {
  Covered: "bg-emerald-50 text-emerald-800 border-emerald-200",
  Partial: "bg-amber-50 text-amber-900 border-amber-200",
  Gap: "bg-rose-50 text-rose-800 border-rose-200",
};
const prettySize = (b) => (b >= 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export function TenderAnalysisPanel({ wizardId, analysis, canDrive, onChanged }) {
  const navigate = useNavigate();
  const [docs, setDocs] = useState([]);
  const [uploads, setUploads] = useState({}); // name -> percent
  const [running, setRunning] = useState(false);
  const [woBusy, setWoBusy] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [contracts, setContracts] = useState([]);
  const fileRef = useRef(null);

  const loadDocs = useCallback(() => {
    api.get(`/wizard/${wizardId}/tender/documents`).then((r) => setDocs(r.data)).catch(() => {});
  }, [wizardId]);
  useEffect(() => { loadDocs(); api.get("/contracts").then((r) => setContracts(r.data)).catch(() => {}); }, [loadDocs]);

  // editable local copy of the analysis
  const [edit, setEdit] = useState(null);
  useEffect(() => {
    if (!analysis) { setEdit(null); return; }
    setEdit({
      proposal_title: analysis.proposal_title || "",
      readiness_summary: analysis.readiness_summary || "",
      intro_draft: analysis.intro_draft || "",
      outline: (analysis.proposal_outline || []).join("\n"),
      matrix: (analysis.compliance_matrix || []).map((m) => ({ ...m })),
    });
  }, [analysis?.generated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const pickFiles = async (files) => {
    const list = Array.from(files || []);
    for (const f of list) {
      const name = f.name.toLowerCase();
      if (!name.endsWith(".pdf") && !name.endsWith(".docx")) { toast.error(`${f.name}: only PDF or DOCX allowed`); continue; }
      if (f.size > 25 * 1024 * 1024) { toast.error(`${f.name}: exceeds 25 MB`); continue; }
      if (docs.length >= 5) { toast.error("Up to 5 documents per pursuit"); break; }
      try {
        setUploads((u) => ({ ...u, [f.name]: 0 }));
        await uploadTenderDocument(wizardId, f, (p) => setUploads((u) => ({ ...u, [f.name]: p })));
        setUploads((u) => { const n = { ...u }; delete n[f.name]; return n; });
        loadDocs();
        toast.success(`${f.name} uploaded`);
      } catch (e) {
        setUploads((u) => { const n = { ...u }; delete n[f.name]; return n; });
        toast.error(e.message || "Upload failed");
      }
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeDoc = async (id) => {
    try { await api.delete(`/wizard/${wizardId}/tender/documents/${id}`); loadDocs(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const run = async () => {
    setRunning(true);
    try {
      await api.post(`/wizard/${wizardId}/tender/analyze`);
      toast.success("Analysis ready");
      await onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setRunning(false); }
  };

  const saveEdits = async () => {
    try {
      await api.patch(`/wizard/${wizardId}/tender/analysis`, {
        proposal_title: edit.proposal_title,
        readiness_summary: edit.readiness_summary,
        intro_draft: edit.intro_draft,
        proposal_outline: edit.outline.split("\n").map((s) => s.trim()).filter(Boolean),
        compliance_matrix: edit.matrix,
      });
      toast.success("Edits saved");
      await onChanged?.();
    } catch (e) { toast.error(apiErr(e)); }
  };

  const usePack = async () => {
    try {
      await api.patch(`/wizard/${wizardId}/tender/analysis`, {
        intro_draft: edit.intro_draft, proposal_title: edit.proposal_title,
      }).catch(() => {});
      const { data } = await api.post(`/wizard/${wizardId}/tender/use-pack`);
      toast.success("Tender Pack pre-filled", { description: "Opening Tender Packs to refine and export." });
      await onChanged?.();
      navigate("/tender");
      return data;
    } catch (e) { toast.error(apiErr(e)); }
  };

  const createGapWo = async (i) => {
    const m = edit.matrix[i];
    const reqLabel = (analysis.requirements || []).find((r) => r.id === m.requirement)?.text || m.requirement;
    setWoBusy(i);
    try {
      const { data } = await api.post(`/wizard/${wizardId}/tender/gap-work-order`, {
        requirement: m.requirement, requirement_label: reqLabel,
        action: m.action || "", status: m.status || "Gap",
      });
      const mx = [...edit.matrix];
      mx[i] = { ...mx[i], work_order_id: data.work_order_id };
      setEdit({ ...edit, matrix: mx });
      toast.success("Draft work order created", { description: "Find it under Project Record → Work orders." });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setWoBusy(null); }
  };

  const bulkGapWos = async () => {
    const rows = (edit?.matrix || [])
      .filter((m) => (m.status === "Gap" || m.status === "Partial") && !m.work_order_id)
      .map((m) => ({
        requirement: m.requirement,
        requirement_label: (analysis.requirements || []).find((r) => r.id === m.requirement)?.text || m.requirement,
        action: m.action || "", status: m.status || "Gap",
      }));
    if (rows.length === 0) { toast.info("No open gaps need a work order"); return; }
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/wizard/${wizardId}/tender/gap-work-orders/bulk`, { rows });
      const map = Object.fromEntries((data.created || []).map((c) => [c.requirement, c.work_order_id]));
      const mx = edit.matrix.map((m) => (map[m.requirement] && !m.work_order_id ? { ...m, work_order_id: map[m.requirement] } : m));
      setEdit({ ...edit, matrix: mx });
      toast.success(`${data.count} work order${data.count === 1 ? "" : "s"} created`, { description: "Find them under Project Record → Work orders." });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBulkBusy(false); }
  };

  const cName = (id) => contracts.find((c) => c.id === id)?.name || id;

  const mx = edit?.matrix || [];
  const rCov = mx.filter((m) => (m.status || "Gap") === "Covered").length;
  const rPar = mx.filter((m) => (m.status || "Gap") === "Partial").length;
  const rGap = mx.filter((m) => (m.status || "Gap") === "Gap").length;
  const readiness = mx.length ? Math.round(((rCov + 0.5 * rPar) / mx.length) * 100) : 0;
  const openGaps = mx.filter((m) => (m.status === "Gap" || m.status === "Partial") && !m.work_order_id).length;
  const tone = readiness >= 75
    ? { bar: "bg-emerald-500", text: "text-emerald-700", ring: "border-emerald-200 bg-emerald-50" }
    : readiness >= 50
    ? { bar: "bg-amber-500", text: "text-amber-700", ring: "border-amber-200 bg-amber-50" }
    : { bar: "bg-rose-500", text: "text-rose-700", ring: "border-rose-200 bg-rose-50" };

  return (
    <Card className="border-sage-light p-5" data-testid={TENDERAI.panel}>
      <div className="mb-1 flex items-center gap-2">
        <FileText className="h-5 w-5 text-forest-mid" />
        <h2 className="font-heading text-lg font-bold text-forest-dark">Tender document analysis</h2>
      </div>
      <p className="mb-4 text-sm text-muted-foreground">Upload the client's tender documents (PDF/DOCX) and let AI extract the requirements and map them to your ROOTS evidence.</p>

      {/* Upload area */}
      {canDrive ? (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); pickFiles(e.dataTransfer.files); }}
          className="rounded-xl border-2 border-dashed border-sage-light bg-sage-surface/40 p-5 text-center"
        >
          <UploadCloud className="mx-auto h-7 w-7 text-forest-mid" />
          <p className="mt-2 text-sm font-medium text-forest-dark">Drop files here or browse</p>
          <p className="text-xs text-muted-foreground">PDF or DOCX · up to 5 files · 25 MB each</p>
          <input ref={fileRef} type="file" accept=".pdf,.docx" multiple className="hidden"
            data-testid={TENDERAI.fileInput} onChange={(e) => pickFiles(e.target.files)} />
          <Button size="sm" variant="outline" className="mt-3" onClick={() => fileRef.current?.click()}
            data-testid={TENDERAI.uploadBtn} disabled={docs.length >= 5}>
            Browse files
          </Button>
        </div>
      ) : (
        <p className="rounded-lg border border-sage-light bg-sage-surface p-3 text-sm text-muted-foreground">You have read-only access to this analysis.</p>
      )}

      {/* uploading progress */}
      {Object.entries(uploads).map(([name, pct]) => (
        <div key={name} className="mt-2 flex items-center gap-3 text-xs">
          <Loader2 className="h-3.5 w-3.5 animate-spin text-forest-mid" />
          <span className="truncate">{name}</span>
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-sage-surface"><div className="h-full bg-forest-mid" style={{ width: `${pct}%` }} /></div>
          <span className="font-mono">{pct}%</span>
        </div>
      ))}

      {/* doc list */}
      {docs.length > 0 && (
        <div className="mt-3 space-y-2">
          {docs.map((d) => (
            <div key={d.id} data-testid={TENDERAI.docRow(d.id)} className="flex items-center justify-between gap-3 rounded-lg border border-sage-light bg-white p-2.5">
              <div className="flex min-w-0 items-center gap-2">
                <FileText className="h-4 w-4 shrink-0 text-forest-mid" />
                <span className="truncate text-sm font-medium text-forest-dark">{d.original_filename}</span>
                <span className="shrink-0 text-xs text-muted-foreground">{prettySize(d.size || 0)}</span>
                {!d.has_text && <span className="flex shrink-0 items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-800"><FileWarning className="h-3 w-3" /> No readable text</span>}
              </div>
              {canDrive && (
                <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-500 hover:text-rose-700" onClick={() => removeDoc(d.id)} data-testid={TENDERAI.removeDoc(d.id)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {canDrive && (
        <Button className="mt-4 bg-forest-mid hover:bg-forest-dark" onClick={run} disabled={running || docs.length === 0} data-testid={TENDERAI.runBtn}>
          {running ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
          {analysis ? "Re-run analysis" : "Run analysis"}
        </Button>
      )}

      {/* Results */}
      {analysis && edit && (
        <div className="mt-5 space-y-5" data-testid={TENDERAI.results}>
          <div className={cn("rounded-xl border p-4", tone.ring)} data-testid={TENDERAI.readiness}>
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Bid readiness</p>
                <p className={cn("font-heading text-3xl font-bold leading-none", tone.text)}>{readiness}%</p>
              </div>
              <p className="text-xs text-muted-foreground">
                <span className="font-semibold text-emerald-700">{rCov}</span> covered ·{" "}
                <span className="font-semibold text-amber-700">{rPar}</span> partial ·{" "}
                <span className="font-semibold text-rose-700">{rGap}</span> gap
              </p>
            </div>
            <div className="mt-2.5 h-2 overflow-hidden rounded-full bg-white/70">
              <div className={cn("h-full rounded-full transition-all", tone.bar)} style={{ width: `${readiness}%` }} />
            </div>
            <p className="mt-1.5 text-[11px] text-muted-foreground">Covered + ½ × partial, across {mx.length} mapped requirement{mx.length === 1 ? "" : "s"}. Updates live as you adjust statuses.</p>
          </div>
          <div className="flex items-start gap-2 rounded-lg border border-sage-light bg-sage-surface p-3 text-xs text-forest-dark">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sage-accent" />
            <span>AI-drafted and human-reviewed. {analysis.model ? `Generated with ${analysis.model}.` : "Automatic summary (AI unavailable)."} Mapped only to your existing ROOTS evidence — anything with no match is flagged as a Gap. Nothing is submitted automatically.</span>
          </div>

          {(analysis.warnings || []).length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              <p className="flex items-center gap-1 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> Limited input</p>
              <ul className="mt-1 list-disc pl-5">{analysis.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
            </div>
          )}

          <Section title="Readiness summary">
            {canDrive ? <Textarea value={edit.readiness_summary} onChange={(e) => setEdit({ ...edit, readiness_summary: e.target.value })} className="min-h-[70px]" /> : <p className="text-sm text-muted-foreground">{edit.readiness_summary || "—"}</p>}
          </Section>

          <Section title="Requirements & evaluation criteria">
            <div className="grid gap-3 sm:grid-cols-2">
              <List label="Requirements" items={(analysis.requirements || []).map((r) => `${r.id ? r.id + ". " : ""}${r.text}`)} />
              <List label="Evaluation criteria" items={(analysis.evaluation_criteria || []).map((c) => `${c.criterion}${c.weighting ? ` — ${c.weighting}` : ""}`)} />
              <List label="Key dates" items={(analysis.key_dates || []).map((d) => `${d.label}: ${d.date}`)} />
              <List label="Submission rules" items={analysis.submission_rules || []} />
            </div>
          </Section>

          <Section title="Compliance & gap matrix">
            {canDrive && openGaps > 0 && (
              <div className="mb-2 flex justify-end">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={bulkGapWos} disabled={bulkBusy} data-testid={TENDERAI.bulkWoBtn}>
                  {bulkBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Hammer className="mr-1.5 h-3.5 w-3.5" />}
                  Create work orders for all gaps ({openGaps})
                </Button>
              </div>
            )}
            <div className="overflow-x-auto rounded-lg border border-sage-light">
              <table className="w-full text-sm">
                <thead className="bg-sage-surface text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr><th className="px-3 py-2">Requirement</th><th className="px-3 py-2 w-28">Status</th><th className="px-3 py-2">ROOTS component / note</th></tr>
                </thead>
                <tbody>
                  {edit.matrix.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-muted-foreground">No requirements mapped.</td></tr>}
                  {edit.matrix.map((m, i) => (
                    <tr key={i} data-testid={TENDERAI.matrixRow(i)} className="border-t border-sage-light align-top">
                      <td className="px-3 py-2">{m.requirement}</td>
                      <td className="px-3 py-2">
                        {canDrive ? (
                          <Select value={m.status || "Gap"} onValueChange={(v) => { const mx = [...edit.matrix]; mx[i] = { ...mx[i], status: v }; setEdit({ ...edit, matrix: mx }); }}>
                            <SelectTrigger className="h-8 w-24" data-testid={TENDERAI.matrixStatus(i)}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="Covered">Covered</SelectItem><SelectItem value="Partial">Partial</SelectItem><SelectItem value="Gap">Gap</SelectItem></SelectContent>
                          </Select>
                        ) : (
                          <span className={cn("inline-block rounded-full border px-2 py-0.5 text-xs font-semibold", STATUS_CHIP[m.status] || STATUS_CHIP.Gap)}>{m.status || "Gap"}</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {canDrive ? (
                          <Input value={m.note || m.component || ""} onChange={(e) => { const mx = [...edit.matrix]; mx[i] = { ...mx[i], note: e.target.value }; setEdit({ ...edit, matrix: mx }); }} className="h-8" />
                        ) : (
                          <span className="text-muted-foreground">{[m.component, m.note].filter(Boolean).join(" — ") || "—"}</span>
                        )}
                        {(m.status === "Gap" || m.status === "Partial") && (
                          <div className={cn("mt-2 rounded-md border p-2", m.status === "Gap" ? "border-rose-200 bg-rose-50/60" : "border-amber-200 bg-amber-50/60")} data-testid={TENDERAI.matrixActionBox(i)}>
                            <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-forest-dark">
                              <ListChecks className="h-3 w-3" /> Next step to close this {m.status === "Gap" ? "gap" : "partial"}
                            </p>
                            {canDrive ? (
                              <Textarea value={m.action || ""} onChange={(e) => { const mx = [...edit.matrix]; mx[i] = { ...mx[i], action: e.target.value }; setEdit({ ...edit, matrix: mx }); }} className="min-h-[52px] text-xs" data-testid={TENDERAI.matrixAction(i)} placeholder="What evidence to build in ROOTS to close this…" />
                            ) : (
                              <p className="text-xs text-muted-foreground">{m.action || "—"}</p>
                            )}
                            <div className="mt-2 flex items-center gap-2">
                              {m.work_order_id ? (
                                <button type="button" onClick={() => navigate("/project?tab=work-orders")} data-testid={TENDERAI.gapWoOpen(i)}
                                  className="inline-flex items-center gap-1 rounded-full border border-forest-mid/30 bg-white px-2.5 py-1 text-[11px] font-semibold text-forest-dark transition-colors hover:border-forest-mid">
                                  <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Draft work order created — open
                                </button>
                              ) : canDrive ? (
                                <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => createGapWo(i)} disabled={woBusy === i} data-testid={TENDERAI.gapWoBtn(i)}>
                                  {woBusy === i ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Hammer className="mr-1 h-3 w-3" />}
                                  Create draft work order
                                </Button>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title="Recommended from your evidence">
            <div className="flex flex-wrap gap-2">
              {(analysis.recommended_contract_ids || []).map((id) => (
                <span key={id} className="rounded-full border border-forest-mid/30 bg-sage-surface px-2.5 py-1 text-xs text-forest-dark">{cName(id)}</span>
              ))}
              {(analysis.recommended_contract_ids || []).length === 0 && <span className="text-xs text-muted-foreground">No contract matched — add verified evidence to strengthen the bid.</span>}
              {(analysis.recommended_case_study_work_order_ids || []).length > 0 && (
                <span className="rounded-full border border-sage-light bg-white px-2.5 py-1 text-xs text-muted-foreground">{analysis.recommended_case_study_work_order_ids.length} case study(ies)</span>
              )}
            </div>
          </Section>

          <Section title="Proposal title">
            {canDrive ? <Input value={edit.proposal_title} onChange={(e) => setEdit({ ...edit, proposal_title: e.target.value })} /> : <p className="text-sm">{edit.proposal_title || "—"}</p>}
          </Section>

          <Section title="Proposal outline">
            {canDrive ? <Textarea value={edit.outline} onChange={(e) => setEdit({ ...edit, outline: e.target.value })} className="min-h-[100px] font-mono text-xs" data-testid={TENDERAI.outlineInput} /> : <ul className="list-disc pl-5 text-sm text-muted-foreground">{(analysis.proposal_outline || []).map((o, i) => <li key={i}>{o}</li>)}</ul>}
          </Section>

          <Section title="Proposal intro (drafted)">
            {canDrive ? <Textarea value={edit.intro_draft} onChange={(e) => setEdit({ ...edit, intro_draft: e.target.value })} className="min-h-[140px]" data-testid={TENDERAI.introInput} /> : <p className="whitespace-pre-wrap text-sm text-muted-foreground">{edit.intro_draft || "—"}</p>}
          </Section>

          {canDrive && (
            <div className="flex flex-wrap gap-2 border-t border-sage-light pt-4">
              <Button variant="outline" onClick={saveEdits} data-testid={TENDERAI.saveEdits}><Save className="mr-1.5 h-4 w-4" /> Save edits</Button>
              <Button className="bg-forest-mid hover:bg-forest-dark" onClick={usePack} data-testid={TENDERAI.usePackBtn}><ArrowUpRight className="mr-1.5 h-4 w-4" /> Use in Tender Pack</Button>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-forest-dark"><CheckCircle2 className="h-3.5 w-3.5 text-sage-accent" /> {title}</h3>
      {children}
    </div>
  );
}

function List({ label, items }) {
  return (
    <div className="rounded-lg border border-sage-light bg-white p-3">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      {(items || []).length === 0 ? <p className="text-xs text-muted-foreground">None found.</p> : (
        <ul className="list-disc space-y-0.5 pl-4 text-sm text-forest-dark">{items.map((it, i) => <li key={i}>{it}</li>)}</ul>
      )}
    </div>
  );
}
